/**
 * Filling the offline mirror while there is a connection (§8.2).
 *
 * Most of the mirror fills itself: every read the app makes is cached as it goes, so
 * whatever you looked at online is there offline. The gaps are the things you have *not*
 * looked at yet — an area you never unfolded, a Wins range you never opened — which would
 * otherwise be missing precisely when you can no longer fetch them.
 *
 * So a few deliberately broad reads are made in the background, chosen to be the ones
 * everything else can be derived from:
 *
 * * the **full task list**, which every filtered task query is computed from offline
 *   (`answerTasksQuery`) — one read that answers every area, project and search;
 * * the **open-tasks snapshot**, which the energy slider filters locally (D167);
 * * **every Wins range**, because a range is derived from the event log and cannot be
 *   computed from another range — four small reads, and the only way the tabs work offline;
 * * **areas, projects, notes and the inbox**, so structure is browsable.
 *
 * All of it is best-effort and never awaited by anything on screen: a failed prime just means
 * the mirror is no fuller than it was.
 */

import { api, request, type Wins, type WinRange } from "./api";
import { TASK_MIRROR_PATH } from "./offlineMirror";

const WIN_RANGES: WinRange[] = ["today", "yesterday", "7d", "30d"];

let priming = false;

/**
 * Top up the mirror. Safe to call often — it is single-flighted, and everything it does is a
 * plain read, so the worst case is a little traffic that was going to be needed anyway.
 */
export async function primeMirror(): Promise<void> {
  if (priming) return;
  priming = true;
  try {
    // Sequential rather than parallel, and deliberately: this runs in the background behind
    // whatever the reader is actually doing, and a burst of eight simultaneous requests would
    // compete with the ones they are waiting on.
    const reads: (() => Promise<unknown>)[] = [
      () => request(TASK_MIRROR_PATH),
      () => api.openTasksSnapshot(),
      () => api.areas(),
      () => api.projects(),
      () => api.notes(),
      () => api.inbox(),
      () => api.starredTasks(),
      // Both windows: "show upcoming" is a separate request, and without it the Routines card
      // reports "nothing further out yet" offline while the routines sit there unmirrored
      // (D170).
      () => api.recurring("today"),
      () => api.recurring("upcoming"),
      ...WIN_RANGES.map((range) => () => api.wins(range) as Promise<Wins>),
    ];
    for (const read of reads) {
      try {
        await read();
      } catch {
        // Offline, or one endpoint unhappy. Either way the rest are still worth trying, and
        // nothing here is load-bearing for what is on screen.
      }
    }
  } finally {
    priming = false;
  }
}
